import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, CheckCircle2, ChevronLeft, ChevronRight, Mail, UserPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { InvitePersonDialog, type InvitePresetScope } from "@/components/InvitePersonDialog";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { formatLastLogin, formatLastLoginAbsolute } from "@/lib/last-login";
import { isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import type { PeopleListResponse, Person, PersonPool, PersonPoolFilter } from "@tilt/admin-sdk";

/**
 * Scoped People tab - the same table the sidebar People page renders,
 * pre-filtered to one partner / organization / location and paired
 * with an "Add person" button that opens InvitePersonDialog locked
 * to this scope.
 *
 * One source of truth: ``apiClient.listPeople`` with a scope filter
 * param (partner_id / organization_id / location_id). The endpoint
 * returns BOTH pools (admin + POS) - operators don't need to remember
 * which pool a teammate signed up under.
 *
 * Used by:
 *   - PartnerDetail → "People" tab (presetScope = partner)
 *   - OrganizationDetail → "People" tab (presetScope = organization)
 *   - LocationDetail → "People" tab (presetScope = location)
 *
 * The full sidebar People page handles cross-scope search + the
 * workplace-popover filter; this tab is intentionally simpler - the
 * scope is already locked by where the operator clicked from.
 */
export function ScopedPeopleTab({
  presetScope,
}: {
  readonly presetScope: InvitePresetScope;
}): JSX.Element {
  const qc = useQueryClient();
  const profile = useRoleProfile();
  const callerIsSuper = isSuperAdmin(profile);
  const [poolFilter, setPoolFilter] = useState<PersonPoolFilter>("all");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    setPageIndex(0);
  }, [poolFilter, includeDisabled]);

  const PAGE_LIMIT = 25;
  const offset = pageIndex * PAGE_LIMIT;

  // Translate the preset into the right scope filter param.
  const scopeParams = useMemo(() => {
    if (presetScope.scope_kind === "partner") return { partner_id: presetScope.partner_id };
    if (presetScope.scope_kind === "organization") return { organization_id: presetScope.scope_id };
    return { location_id: presetScope.scope_id };
  }, [presetScope]);

  const query = useQuery<PeopleListResponse>({
    queryKey: [
      "scoped-people",
      presetScope.scope_kind,
      presetScope.scope_id,
      { poolFilter, includeDisabled, offset },
    ],
    queryFn: () =>
      apiClient.listPeople({
        ...scopeParams,
        limit: PAGE_LIMIT,
        offset,
        pool: poolFilter,
        ...(includeDisabled ? { include_disabled: true } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const data = query.data;
  const items: Person[] = data?.people ?? [];
  const total = data?.total ?? 0;
  const byPool = data?.by_pool ?? { admin: 0, pos: 0 };
  const totalAcrossPools = byPool.admin + byPool.pos;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const hasPrev = pageIndex > 0;
  const hasNext = pageIndex + 1 < pageCount;
  const showingStart = total === 0 ? 0 : offset + 1;
  const showingEnd = offset + items.length;

  const onMutated = (): void => {
    void qc.invalidateQueries({ queryKey: ["scoped-people"] });
    void qc.invalidateQueries({ queryKey: ["people"] });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">People</h2>
          <p className="text-xs text-muted-foreground">
            Everyone with a membership at this {presetScope.scope_kind} (or anywhere below it). Both
            admin console and POS application accounts.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInviting(true)}
          className="inline-flex flex-shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Add person
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border bg-background p-1 text-xs">
          <PoolChip
            label="All"
            count={totalAcrossPools}
            active={poolFilter === "all"}
            onClick={() => setPoolFilter("all")}
          />
          <PoolChip
            label="Admin console"
            count={byPool.admin}
            active={poolFilter === "admin"}
            onClick={() => setPoolFilter("admin")}
          />
          <PoolChip
            label="POS"
            count={byPool.pos}
            active={poolFilter === "pos"}
            onClick={() => setPoolFilter("pos")}
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => setIncludeDisabled(e.target.checked)}
          />
          Include disabled
        </label>
        {query.isFetching && !query.isPending && (
          <span className="text-xs text-muted-foreground" aria-live="polite">
            Updating…
          </span>
        )}
      </div>

      {query.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          Failed to load people: {describe(query.error)}
        </div>
      )}
      {query.isPending ? (
        <p className="rounded-lg border border-dashed bg-muted/20 p-5 text-center text-sm text-muted-foreground">
          Loading people…
        </p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          No people at this {presetScope.scope_kind} yet. Use Add person to invite someone.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5">Person</th>
                <th className="px-4 py-2.5">Apps</th>
                <th className="px-4 py-2.5">Roles</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Last login</th>
                <th className="px-4 py-2.5 text-right" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <PersonRow key={`${p.pool}:${p.id}`} person={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_LIMIT && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-2 text-xs text-muted-foreground">
          <span>{total === 0 ? "-" : `Showing ${showingStart}–${showingEnd} of ${total}`}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              disabled={!hasPrev || query.isPending}
              className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-3 w-3" /> Prev
            </button>
            <span>
              Page {pageIndex + 1} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPageIndex((i) => i + 1)}
              disabled={!hasNext || query.isPending}
              className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {inviting && (
        <InvitePersonDialog
          presetScope={presetScope}
          callerIsSuperAdmin={callerIsSuper}
          onClose={() => setInviting(false)}
          onCreated={() => {
            onMutated();
            setInviting(false);
          }}
        />
      )}
    </div>
  );
}

const APP_LABEL: Record<PersonPool, string> = {
  admin: "Admin console",
  pos: "POS",
};

function PersonRow({ person }: { readonly person: Person }) {
  const fullName = [person.first_name, person.last_name].filter(Boolean).join(" ");
  const isAdmin = person.pool === "admin";
  return (
    <tr className="border-t hover:bg-muted/30">
      <td className="px-4 py-2 align-middle">
        <div className="font-medium text-slate-900">{fullName || person.email}</div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Mail className="h-3 w-3" />
          {person.email}
        </div>
      </td>
      <td className="px-4 py-2 align-middle">
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            isAdmin ? "bg-indigo-100 text-indigo-800" : "bg-emerald-100 text-emerald-800",
          )}
        >
          {APP_LABEL[person.pool]}
        </span>
      </td>
      <td className="px-4 py-2 align-middle">
        <div className="flex flex-wrap gap-1">
          {person.roles_summary.length === 0 ? (
            <span className="text-xs text-muted-foreground">No roles</span>
          ) : (
            person.roles_summary.slice(0, 4).map((r) => (
              <span
                key={r}
                className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
              >
                {r.replace(/_/g, " ")}
              </span>
            ))
          )}
          {person.roles_summary.length > 4 && (
            <span className="text-[10px] text-muted-foreground">
              +{person.roles_summary.length - 4} more
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 align-middle">
        {person.is_disabled ? (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700">
            <Ban className="h-3 w-3" />
            Disabled
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 className="h-3 w-3" />
            Active
          </span>
        )}
      </td>
      <td
        className={cn(
          "px-4 py-2 align-middle text-xs",
          person.last_active_at ? "text-slate-700" : "text-muted-foreground",
        )}
        title={formatLastLoginAbsolute(person.last_active_at)}
      >
        {formatLastLogin(person.last_active_at)}
      </td>
      <td className="px-4 py-2 text-right align-middle">
        <Link
          to={`/people/${person.pool}/${person.id}`}
          className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          title={`Manage ${person.email}`}
        >
          Manage
        </Link>
      </td>
    </tr>
  );
}

function PoolChip({
  label,
  count,
  active,
  onClick,
}: {
  readonly label: string;
  readonly count: number;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded px-2 py-0.5 font-medium",
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100",
      )}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );
}
